/**
 * Shared building blocks for the public marketing pages (Home, About,
 * Services, Contact). Keeps the rhythm consistent: tracked eyebrow, a modern
 * legible sans title (the global Zarathos heading face is reserved for the
 * brand wordmark), generous space, hairline borders. See DESIGN.md.
 */
import type { ReactNode } from 'react';
import { Scissors, Crown, Paintbrush, Droplets } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { Service } from '@/types';

/** Full-bleed page header used at the top of every public page. */
export function PageHero({
  eyebrow,
  title,
  subtitle,
  children,
}: {
  eyebrow?: string;
  title: ReactNode;
  subtitle?: string;
  children?: ReactNode;
}) {
  return (
    <section className="border-b border-hairline bg-canvas pt-32 md:pt-40">
      <div className="mx-auto max-w-6xl px-6 pb-16 md:pb-20">
        <div className="mx-auto max-w-3xl text-center">
          {eyebrow && (
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-ink-subtle">{eyebrow}</p>
          )}
          <h1 className="mt-4 font-sans text-4xl font-semibold tracking-tight text-ink md:text-6xl">
            {title}
          </h1>
          {subtitle && (
            <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-ink-muted md:text-xl">
              {subtitle}
            </p>
          )}
          {children && <div className="mt-8">{children}</div>}
        </div>
      </div>
    </section>
  );
}

/** In-page section header: tracked eyebrow, sans title, muted subtitle. */
export function SectionHeading({
  eyebrow,
  title,
  subtitle,
  align = 'center',
}: {
  eyebrow?: string;
  title: ReactNode;
  subtitle?: string;
  align?: 'center' | 'left';
}) {
  return (
    <div className={align === 'center' ? 'mx-auto max-w-2xl text-center' : 'max-w-2xl'}>
      {eyebrow && (
        <p className="text-xs font-medium uppercase tracking-[0.22em] text-ink-subtle">{eyebrow}</p>
      )}
      <h2 className="mt-4 font-sans text-3xl font-semibold tracking-tight text-ink md:text-4xl">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-4 text-base leading-relaxed text-ink-muted md:text-lg">{subtitle}</p>
      )}
    </div>
  );
}

/** Map a service to a thin monochrome lucide icon (no emoji — see DESIGN.md). */
export function getServiceIcon(service: Service): LucideIcon {
  const haystack = `${service.name} ${service.category || ''}`.toLowerCase();
  if (/(vip|premium|gold|lux)/.test(haystack)) return Crown;
  if (/(tinte|color|mech)/.test(haystack)) return Paintbrush;
  if (/(tratamiento|spa|facial|mascar|shave|afeit)/.test(haystack)) return Droplets;
  return Scissors;
}
